/**
 * Global scroll-restoration for top-level routes.
 *
 * Saves window.scrollY per pathname in sessionStorage and restores it on
 * return. Project-builder tabs have their own scroll persistence inside
 * `ProjectBuilder` (sessionStorage key `eengdes:scroll:<pid>:<tab>`); this
 * hook handles every OTHER route (Dashboard, Library, Calculators, Orgs,
 * Settings, etc.).
 *
 * Usage: drop <ScrollKeeper/> once inside the <BrowserRouter>.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const KEY = (path) => `eengdes:scrollY:${path}`;

export default function ScrollKeeper() {
  const { pathname } = useLocation();

  // Save scrollY whenever the user scrolls (rAF-throttled).
  useEffect(() => {
    let raf = null;
    const save = () => {
      try { sessionStorage.setItem(KEY(pathname), String(window.scrollY || 0)); } catch {}
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { save(); raf = null; });
    };
    // Cover full-reload navigation too (React useEffect cleanup may not
    // fire before the page actually unloads).
    const onPageHide = () => save();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onPageHide);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onPageHide);
      if (raf) cancelAnimationFrame(raf);
      // Do NOT save in cleanup — by the time cleanup runs, the browser may
      // have already reset scrollY to 0 (e.g., on SPA navigation that
      // re-mounts the body), which would overwrite the correct saved value.
    };
  }, [pathname]);

  // Restore on route entry. Retry several frames since data-fetching pages
  // may grow after mount. We poll for ~1.6s; bail early if user scrolls.
  useEffect(() => {
    const sav = (() => { try { return sessionStorage.getItem(KEY(pathname)); } catch { return null; } })();
    const target = Number(sav) || 0;
    if (target <= 0) return;
    let cancelled = false;
    let userScrolled = false;
    const stopWheel = () => { userScrolled = true; };
    window.addEventListener("wheel",      stopWheel, { passive: true, once: true });
    window.addEventListener("touchstart", stopWheel, { passive: true, once: true });
    window.addEventListener("keydown",    stopWheel, { once: true });

    const start = performance.now();
    const tryScroll = () => {
      if (cancelled || userScrolled) return;
      const max = (document.documentElement.scrollHeight || 0) - window.innerHeight;
      const dest = Math.min(target, Math.max(0, max));
      if (Math.abs(window.scrollY - dest) > 1) window.scrollTo(0, dest);
      // Always retry through the full window (data-loading pages can shrink
      // and re-grow, causing the browser to clamp scrollY back to 0 after
      // the user's initial restore succeeded).
      if (performance.now() - start < 1600) {
        setTimeout(tryScroll, 80);
      }
    };
    requestAnimationFrame(tryScroll);
    return () => {
      cancelled = true;
      window.removeEventListener("wheel",      stopWheel);
      window.removeEventListener("touchstart", stopWheel);
      window.removeEventListener("keydown",    stopWheel);
    };
  }, [pathname]);

  return null;
}
